import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { BillingStore } from '../../core/stores/billing.store';
import { FloorStore } from '../../core/stores/floor.store';
import { PermissionService } from '../../core/permissions/permission.service';
import { NvIcon } from '../../shared/ui/icon/nv-icon';
import { formatInr } from '../../shared/utils/money.util';
import { toMillis } from '../../shared/utils/timestamp.util';
import { environment } from '../../../environments/environment';
import { SHIFT_LABEL, shiftAt, startOfDay } from './shift-window';

/**
 * The status strip at the bottom of the content column — console redesign
 * (Cafooze POS prototype `<footer>`): current shift, today's sales and
 * guests served, cash in drawer, free tables, and a link to the day summary.
 *
 * A static strip now: the earlier cycling shift chip and the expandable
 * "day view" panel are gone — the day summary link opens the full numbers
 * in Reports instead of repeating them here.
 *
 * Mounted in shell.html as a normal flex sibling of `.nv-shell__content`,
 * so it sits in the page flow at the bottom of the column.
 */
@Component({
  selector: 'nv-shift-footer',
  imports: [RouterLink, NvIcon],
  templateUrl: './shift-footer.html',
  styleUrl: './shift-footer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShiftFooter {
  private readonly billing = inject(BillingStore);
  private readonly floor = inject(FloorStore);
  private readonly permissions = inject(PermissionService);

  readonly visible = computed(() => environment.previewFeatures && this.permissions.can('manage_orders'));

  /** Reports → Summary → Day, gated the same way the Insights route is. */
  readonly canSeeDaySummary = computed(() => environment.previewFeatures && this.permissions.can('view_flow_reports'));

  private readonly now = signal(Date.now());

  readonly shiftLabel = computed(() => SHIFT_LABEL[shiftAt(this.now())]);

  readonly salesLabel = computed(() => formatInr(this.billing.collectedTodayMinor()));

  /** Party sizes on today's settled bills — guests who have already paid and left. */
  readonly guestsServed = computed(() => {
    const day0 = startOfDay(this.now());
    return this.billing
      .bills()
      .filter((b) => b.status === 'settled' && (toMillis(b.settledAt ?? b.createdAt) ?? 0) >= day0)
      .reduce((sum, b) => sum + (b.coversCount ?? 0), 0);
  });

  /** Deliberately NOT scoped to any one shift — physical cash sitting in a till doesn't reset at a shift boundary. */
  readonly cashInDrawerLabel = computed(() => formatInr(this.billing.paymentsByMode().get('cash') ?? 0));

  readonly freeCount = computed(() => this.floor.stateCounts().free);
  readonly totalTables = this.floor.totalCount;

  constructor() {
    const tick = setInterval(() => this.now.set(Date.now()), 60_000);
    inject(DestroyRef).onDestroy(() => clearInterval(tick));
  }
}
